import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useTableUpdates } from '../hooks/useWebSocket';
import AvailabilityGrid from '../components/reservations/AvailabilityGrid';
import { DateTimeSelector } from '../components/reservations/ReservationForm';
import { ReservationSummary, MyReservationsList } from '../components/reservations/ReservationSummary';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';

export default function TableReservation() {
  // Form state
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [partySize, setPartySize] = useState(2); // max 10 (call store for larger groups)
  const [notes, setNotes] = useState('');

  // Reservations and UI state
  const [myReservations, setMyReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { user } = useAuth();
  const { t, lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  const reloadReservations = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API}/tables/my`)
      .then((r) => setMyReservations(r.data || []))
      .catch(() => {});
  }, [user]);

  const reloadAvailability = useCallback((date) => {
    if (!date) return Promise.resolve();
    return axios.get(`${API}/tables/availability`, { params: { date } })
      .then((r) => setAvailability(r.data))
      .catch(() => {});
  }, []);

  // Real-time updates handler
  const handleRealtimeUpdate = useCallback((data) => {
    if (selectedDate && data.data?.date === selectedDate) {
      reloadAvailability(selectedDate);
    }
    if (user) {
      reloadReservations();
    }
  }, [reloadAvailability, reloadReservations, selectedDate, user]);

  const { isConnected } = useTableUpdates(handleRealtimeUpdate);

  // Initial data fetch
  useEffect(() => {
    reloadReservations();
  }, [reloadReservations]);

  // Fetch availability when date changes
  useEffect(() => {
    reloadAvailability(selectedDate);
  }, [reloadAvailability, selectedDate]);

  // Auto-select tables based on party size + slot + date
  useEffect(() => {
    if (!selectedDate || !selectedSlot) {
      setSelectedTables([]);
      return;
    }
    axios.get(`${API}/reservations/tables/suggest`, {
      params: { date: selectedDate, time_slot: selectedSlot, duration, party_size: partySize }
    }).then(r => {
      if (r.data.suggestions?.length > 0) {
        setSelectedTables(r.data.suggestions[0].tables.map(t => t.table_number));
      } else {
        setSelectedTables([]);
      }
    }).catch(() => setSelectedTables([]));
  }, [selectedDate, selectedSlot, partySize, duration]);

  const getTotalCapacity = () => {
    if (!availability) return 0;
    return selectedTables.reduce((sum, tableNum) => {
      const table = availability.tables?.find(t => t.table_number === tableNum);
      return sum + (table?.capacity || 0);
    }, 0);
  };

  // Handlers
  const handleConfirm = async () => {
    if (!user) { window.location.href = '/auth'; return; }
    if (selectedTables.length === 0 || !selectedDate || !selectedSlot) {
      setError(text('Selecciona al menos una mesa', 'Select at least one table'));
      return;
    }

    const totalCapacity = getTotalCapacity();
    if (totalCapacity < partySize) {
      setError(text(
        `Capacidad insuficiente (${totalCapacity}). Selecciona más mesas.`,
        `Insufficient capacity (${totalCapacity}). Select more tables.`,
      ));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API}/tables/reserve`, {
        table_numbers: selectedTables,
        date: selectedDate,
        time_slot: selectedSlot,
        duration_hours: duration,
        party_size: partySize,
        notes: notes || undefined
      });
      setSuccess(text(
        `¡${selectedTables.length} mesa(s) reservada(s)!`,
        `${selectedTables.length} table(s) booked!`,
      ));
      setSelectedSlot('');
      setSelectedTables([]);
      await reloadReservations();
      await reloadAvailability(selectedDate);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const handleCancelTable = async (id) => {
    try {
      await axios.delete(`${API}/tables/${id}`);
      setMyReservations(prev => prev.map(r => r.reservation_id === id ? { ...r, status: 'cancelled' } : r));
    } catch (err) {
      console.error('Failed to cancel table reservation:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-black text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
          {t('reservations.table_title')}
        </h1>
        <ConnectionIndicator isConnected={isConnected} lang={lang} />
      </div>
      <p className="text-[#A8B5AE] mb-2">
        {text(
          'Reserva una mesa. Las mesas se asignan automáticamente según el número de personas.',
          'Book a table. Tables are automatically assigned based on your party size.',
        )}
      </p>
      {/* Call us note */}
      <div className="mb-6 px-4 py-2.5 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-xl text-sm text-[#C9A962]">
        📞 {text(
          'Para reservas específicas o grupos de más de 10 personas, llama directamente a la tienda.',
          'For specific reservations or groups over 10 people, call the store directly.',
        )}
      </div>

      {/* Compact Availability Grid (display only) */}
      <AvailabilityGrid
        selectedDate={selectedDate}
        selectedSlot={selectedSlot}
        availability={availability}
        lang={lang}
        onSelectSlot={(slot) => setSelectedSlot(slot)}
        durationHours={duration}
        previewReservedTableNumbers={selectedTables}
      />

      {/* Main Form Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Date & Time Selection */}
        <DateTimeSelector
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedSlot={selectedSlot}
          setSelectedSlot={setSelectedSlot}
          duration={duration}
          setDuration={setDuration}
          partySize={partySize}
          setPartySize={setPartySize}
          setSelectedTables={setSelectedTables}
          t={t}
          lang={lang}
        />

        {/* Auto-assigned table info */}
        <div className="space-y-4">
          <div>
            <h3 className="text-[#F5F0E6] font-bold mb-3 text-sm uppercase tracking-wider text-[#C9A962]">
              {text('Mesa Asignada', 'Assigned Table')}
            </h3>
            {!selectedSlot ? (
              <p className="text-[#52525B] text-sm">
                {text('Selecciona fecha y hora para ver la asignación', 'Select date and time to see assignment')}
              </p>
            ) : selectedTables.length === 0 ? (
              <div className="p-3 bg-[#C45B4A]/10 border border-[#C45B4A]/30 rounded-xl">
                <p className="text-[#F87171] text-sm font-semibold">
                  {text('Sin disponibilidad', 'No availability')}
                </p>
                <p className="text-[#A8B5AE] text-xs mt-1">
                  {text('Prueba otra hora o reduce el grupo', 'Try another time or reduce party size')}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-[#2D8B70]/10 border border-[#2D8B70]/30 rounded-xl">
                <p className="text-[#2D8B70] text-sm font-semibold mb-1">
                  ✓ {text('Mesa disponible', 'Table available')}
                </p>
                {availability?.tables
                  ?.filter(t => selectedTables.includes(t.table_number))
                  .map(t => (
                    <p key={t.table_number} className="text-[#A8B5AE] text-xs">
                      Mesa {t.table_number} — {getLocalizedField(t, 'name', lang, t.name)} ({t.capacity} {text('pers.', 'ppl')})
                    </p>
                  ))
                }
              </div>
            )}
          </div>
          {/* Notes */}
          <div>
            <label className="text-[#A8B5AE] text-xs font-semibold uppercase tracking-wider mb-2 block">
              {text('Notas (opcional)', 'Notes (optional)')}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={text('Peticiones especiales...', 'Special requests...')}
              rows={3}
              className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] resize-none placeholder:text-[#A8B5AE]/50"
            />
          </div>
        </div>

        {/* Summary & Confirm */}
        <div className="space-y-6">
          <ReservationSummary
            selectedTables={selectedTables}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            duration={duration}
            partySize={partySize}
            totalCapacity={getTotalCapacity()}
            error={error}
            success={success}
            loading={loading}
            onConfirm={handleConfirm}
            t={t}
            lang={lang}
          />
        </div>
      </div>

      {/* My Reservations */}
      {user && (
        <MyReservationsList
          reservations={myReservations}
          onCancel={handleCancelTable}
          t={t}
          lang={lang}
        />
      )}
    </div>
  );
}

function ConnectionIndicator({ isConnected, lang }) {
  const text = (es, en) => pickLocalizedText(lang, es, en);
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
      isConnected ? 'bg-[#2D8B70]/20 text-[#2D8B70]' : 'bg-[#C45B4A]/20 text-[#F87171]'
    }`}>
      {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
      {isConnected
        ? text('En vivo', 'Live')
        : text('Desconectado', 'Offline')
      }
    </div>
  );
}
